import { resolveHdPlanetLineFixing, HdLineFixingDuality } from '../hd-line-fixing.utils';
import { getHdHarmonicGatesOf } from '../gate-info/line/hd-gate-line.view-model';
import { HdBodygraphPlanetaryActivation } from '../hd-bodygraph.models';
import { getHdPlanetDisplayConfigByPlanetId, HdFixingState } from './hd-planet-display-config';
import { resolveHdFixingState } from './hd-planet-grid.utils';

export type HdJustNowFixingRelation = 'same-gate' | 'harmonic-gate';

export interface HdJustNowFixingCause {
  readonly activationId: string;
  readonly planetId: number;
  readonly gate: number;
  readonly relation: HdJustNowFixingRelation;
  readonly fixing: Exclude<HdFixingState, 'none' | 'both'>;
}

export interface HdJustNowBaseFixingPresentation {
  readonly baseFixing: HdFixingState;
  readonly transitFixing: HdFixingState;
  readonly transitCauses: readonly HdJustNowFixingCause[];
}

export type HdJustNowLineDataResolver = (gate: number, line: number) => HdLineFixingDuality | null;

export function resolveHdJustNowTransitFixing(
  activation: HdBodygraphPlanetaryActivation,
  lineData: HdJustNowLineDataResolver,
): HdFixingState {
  const duality = lineData(activation.gate, activation.line);
  if (duality == null) {
    return 'none';
  }
  const planetKey = getHdPlanetDisplayConfigByPlanetId(activation.planetId)?.key ?? null;
  return resolveHdPlanetLineFixing(planetKey, duality);
}

export function resolveHdJustNowBaseFixingPresentation(
  baseActivation: HdBodygraphPlanetaryActivation,
  transitActivations: readonly HdBodygraphPlanetaryActivation[],
  lineData: HdJustNowLineDataResolver,
): HdJustNowBaseFixingPresentation {
  const duality = lineData(baseActivation.gate, baseActivation.line);
  if (duality == null) {
    return {
      baseFixing: resolveHdFixingState(baseActivation),
      transitFixing: 'none',
      transitCauses: [],
    };
  }

  const harmonicGates = new Set(getHdHarmonicGatesOf(baseActivation.gate));
  const causes = new Map<string, HdJustNowFixingCause>();

  for (const transitActivation of transitActivations) {
    const relation = resolveRelation(baseActivation, transitActivation, harmonicGates);
    if (relation == null) {
      continue;
    }
    const planetKey = getHdPlanetDisplayConfigByPlanetId(transitActivation.planetId)?.key ?? null;
    const fixing = resolveHdPlanetLineFixing(planetKey, duality);
    if (fixing === 'none') {
      continue;
    }
    if (fixing === 'exaltation' || fixing === 'both') {
      causes.set(`${transitActivation.id}:exaltation`, buildCause(transitActivation, relation, 'exaltation'));
    }
    if (fixing === 'detriment' || fixing === 'both') {
      causes.set(`${transitActivation.id}:detriment`, buildCause(transitActivation, relation, 'detriment'));
    }
  }

  const transitCauses = [...causes.values()];
  return {
    baseFixing: resolveHdFixingState(baseActivation),
    transitFixing: combineFixingCauses(transitCauses),
    transitCauses,
  };
}

function resolveRelation(
  baseActivation: HdBodygraphPlanetaryActivation,
  transitActivation: HdBodygraphPlanetaryActivation,
  harmonicGates: ReadonlySet<number>,
): HdJustNowFixingRelation | null {
  if (transitActivation.gate === baseActivation.gate) {
    return transitActivation.line === baseActivation.line ? 'same-gate' : null;
  }
  return harmonicGates.has(transitActivation.gate) ? 'harmonic-gate' : null;
}

function buildCause(
  activation: HdBodygraphPlanetaryActivation,
  relation: HdJustNowFixingRelation,
  fixing: 'exaltation' | 'detriment',
): HdJustNowFixingCause {
  return {
    activationId: activation.id,
    planetId: activation.planetId,
    gate: activation.gate,
    relation,
    fixing,
  };
}

function combineFixingCauses(causes: readonly HdJustNowFixingCause[]): HdFixingState {
  const hasExaltation = causes.some(cause => cause.fixing === 'exaltation');
  const hasDetriment = causes.some(cause => cause.fixing === 'detriment');
  if (hasExaltation && hasDetriment) {
    return 'both';
  }
  if (hasExaltation) {
    return 'exaltation';
  }
  return hasDetriment ? 'detriment' : 'none';
}
